import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Camera, CheckCircle, XCircle, AlertCircle, Upload, Eye, Trash2, ArrowRight, Pencil, Save, X, Database } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface WorksheetScannerProps {
  county: string;
  onRecordsUpdated?: () => void;
  onViewUpdatedRecords?: () => void;
}

interface ScanResult {
  doc: string;
  dbDoc?: string;
  dbId?: string;
  name?: string;
  dbName?: string;
  address?: string;
  existingAddress?: string | null;
  col5?: string;
  matchMethod?: string;
  filingDate?: string;
  status: 'matched' | 'skipped' | 'not_found' | 'error' | 'saved';
  reason?: string;
}

function normalizeLookupDate(value?: string) {
  if (!value) return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (slashMatch) {
    const [, month, day, rawYear] = slashMatch;
    const year = rawYear.length === 2
      ? `${Number(rawYear) > 50 ? '19' : '20'}${rawYear}`
      : rawYear;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  return trimmed;
}

export function WorksheetScanner({ county, onRecordsUpdated, onViewUpdatedRecords }: WorksheetScannerProps) {
  const [imageData, setImageData] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [results, setResults] = useState<{ matched: number; skipped: number; notFound: number; details: ScanResult[] } | null>(null);
  const [totalSaved, setTotalSaved] = useState(0);
  const [sheetsScanned, setSheetsScanned] = useState(0);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [editName, setEditName] = useState("");
  const [editDate, setEditDate] = useState("");
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [integrated, setIntegrated] = useState(false);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => setImageData(event.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const blob = items[i].getAsFile();
        if (blob) {
          const reader = new FileReader();
          reader.onload = (event) => setImageData(event.target?.result as string);
          reader.readAsDataURL(blob);
        }
      }
    }
  };

  const handleScan = async () => {
    if (!imageData) return;
    setIsProcessing(true);
    setResults(null);
    setIntegrated(false);

    try {
      const { data, error } = await supabase.functions.invoke("scan-address-worksheet", {
        body: { imageData, county },
      });

      if (error) throw error;

      if (data.success) {
        setResults(data.results);
        setSheetsScanned(prev => prev + 1);
        const matchCount = data.results.matched || 0;
        toast.success(`Sheet ${sheetsScanned + 1}: Found ${matchCount} matches — review and click "Save to Database" to commit`);
      } else {
        throw new Error(data.error || "Scan failed");
      }
    } catch (error: any) {
      console.error("Scan error:", error);
      toast.error(error.message || "Failed to scan worksheet");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleIntegrate = async () => {
    if (!results) return;

    const candidates = results.details
      .filter(r => (r.status === 'matched' || r.status === 'not_found') && r.address);

    if (candidates.length === 0) {
      toast.error("No matched records with addresses to save");
      return;
    }

    setIsSaving(true);
    try {
      const resolvedRecords: { dbId: string; address: string; name?: string }[] = [];
      const updatedDetails = [...results.details];
      let lookupFailures = 0;
      const failedNames: string[] = [];

      for (const r of candidates) {
        let dbId = r.dbId;

        if (!dbId) {
          if (r.name) {
            const namePrefix = r.name.includes(',') ? r.name.split(',')[0].trim() : r.name.split(/\s+/)[0].trim();
            const normalizedDate = normalizeLookupDate(r.filingDate) ?? (r.doc && /^\d{4}-\d{2}-\d{2}$/.test(r.doc) ? r.doc : undefined);
            let query = supabase
              .from('county_research')
              .select('id')
              .ilike('grantor_name', `${namePrefix}%`)
              .eq('county', county.toLowerCase())
              .is('deleted_at', null);

            if (normalizedDate) {
              query = query.eq('filing_date', normalizedDate);
            }

            const { data } = await query.limit(1);
            if (data && data.length > 0) {
              dbId = data[0].id;
              const idx = results.details.indexOf(r);
              if (idx >= 0) updatedDetails[idx] = { ...updatedDetails[idx], dbId, filingDate: normalizedDate ?? updatedDetails[idx].filingDate };
            }
          }

          if (!dbId && r.dbName) {
            const normalizedDate = normalizeLookupDate(r.filingDate);
            let fallbackQuery = supabase
              .from('county_research')
              .select('id')
              .ilike('grantor_name', r.dbName)
              .eq('county', county.toLowerCase())
              .is('deleted_at', null);

            if (normalizedDate) {
              fallbackQuery = fallbackQuery.eq('filing_date', normalizedDate);
            }

            const { data } = await fallbackQuery.limit(1);
            if (data && data.length > 0) {
              dbId = data[0].id;
              const idx = results.details.indexOf(r);
              if (idx >= 0) updatedDetails[idx] = { ...updatedDetails[idx], dbId, filingDate: normalizedDate ?? updatedDetails[idx].filingDate };
            }
          }
        }

        if (dbId) {
          resolvedRecords.push({ dbId, address: r.address!, name: r.name });
        } else {
          failedNames.push(r.name || 'Unknown');
          lookupFailures++;
          console.warn(`Could not find DB record for "${r.name}" — skipping`);
        }
      }

      if (resolvedRecords.length === 0) {
        toast.error("Could not find any matching database records");
        setIsSaving(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke("scan-address-worksheet", {
        body: { integrate: true, records: resolvedRecords },
      });

      if (error) throw error;

      if (data.success) {
        setTotalSaved(prev => prev + data.saved);
        setIntegrated(true);

        const savedDbIds = new Set(resolvedRecords.map(rr => rr.dbId));
        const finalDetails = updatedDetails.map(r => 
          (r.status === 'matched' || r.status === 'not_found') && r.address && (r.dbId && savedDbIds.has(r.dbId))
            ? { ...r, status: 'saved' as const } : r
        );
        setResults({ ...results, details: finalDetails });

        if (lookupFailures > 0) {
          toast.warning(`⚠️ ${lookupFailures} record${lookupFailures > 1 ? 's' : ''} could not be matched: ${failedNames.join(', ')}`, { duration: 8000 });
        }
        toast.success(`✅ Saved ${data.saved} of ${candidates.length} addresses to database`);
        onRecordsUpdated?.();
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to save to database");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearResults = () => {
    setResults(null);
    setImageData(null);
    setIntegrated(false);
    setEditingIndex(null);
    toast.info("Scan results cleared (database not affected)");
  };

  const handleNextSheet = () => {
    setImageData(null);
    setResults(null);
    setIntegrated(false);
    setEditingIndex(null);
  };

  const startEdit = (index: number, currentAddress: string | undefined) => {
    const row = results?.details[index];
    setEditingIndex(index);
    setEditValue(currentAddress || "");
    setEditName(row?.name || row?.dbName || "");
    setEditDate(row?.filingDate || "");
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditValue("");
    setEditName("");
    setEditDate("");
  };

  const saveEdit = async (index: number) => {
    if (!results) return;
    const row = results.details[index];

    if (integrated && row.dbId) {
      setSavingIndex(index);
      const newAddress = editValue.trim() || null;
      try {
        const { error } = await supabase
          .from('county_research')
          .update({
            grantor_address: newAddress,
            address_extraction_method: newAddress ? 'worksheet_scan_manual' : null,
            address_needs_review: false,
          })
          .eq('id', row.dbId);
        if (error) throw error;
        
        const updatedDetails = [...results.details];
        updatedDetails[index] = { ...updatedDetails[index], address: newAddress || undefined, status: newAddress ? 'saved' : 'skipped' };
        setResults({ ...results, details: updatedDetails });
        setEditingIndex(null);
        setEditValue("");
        setEditName("");
        setEditDate("");
        toast.success(`Address updated in database`);
        onRecordsUpdated?.();
      } catch (error: any) {
        toast.error(error.message || "Failed to save");
      } finally {
        setSavingIndex(null);
      }
      return;
    }

    const updatedDetails = [...results.details];
    const newAddress = editValue.trim() || null;
    updatedDetails[index] = {
      ...updatedDetails[index],
      address: newAddress || undefined,
      status: newAddress ? 'matched' : 'skipped',
    };
    const matched = updatedDetails.filter(d => d.status === 'matched').length;
    const skipped = updatedDetails.filter(d => d.status === 'skipped').length;
    setResults({ ...results, details: updatedDetails, matched, skipped });
    setEditingIndex(null);
    setEditValue("");
    setEditName("");
    setEditDate("");
    toast.success("Address updated locally — click 'Save to Database' to commit");
  };

  const saveEditWithLookup = async (index: number) => {
    if (!results) return;
    const row = results.details[index];
    const newAddress = editValue.trim() || null;
    const lookupName = editName.trim() || row.dbName || row.name;
    const lookupDate = editDate.trim() || row.filingDate;
    const normalizedDate = normalizeLookupDate(lookupDate);
    let dbId = row.dbId;

    if (!dbId && lookupName) {
      let query = supabase
        .from('county_research')
        .select('id, grantor_name')
        .ilike('grantor_name', `${lookupName}%`)
        .eq('county', county.toLowerCase())
        .is('deleted_at', null);
      if (normalizedDate) {
        query = query.eq('filing_date', normalizedDate);
      }
      const { data } = await query.limit(1);
      if (data && data.length > 0) dbId = data[0].id;
    }

    if (!dbId && row.dbName) {
      let fallbackQuery = supabase
        .from('county_research')
        .select('id')
        .ilike('grantor_name', row.dbName)
        .eq('county', county.toLowerCase())
        .is('deleted_at', null);
      if (normalizedDate) {
        fallbackQuery = fallbackQuery.eq('filing_date', normalizedDate);
      }
      const { data } = await fallbackQuery.limit(1);
      if (data && data.length > 0) dbId = data[0].id;
    }

    if (!dbId) {
      const updatedDetails = [...results.details];
      updatedDetails[index] = {
        ...updatedDetails[index],
        name: lookupName || updatedDetails[index].name,
        filingDate: normalizedDate || lookupDate || updatedDetails[index].filingDate,
        address: newAddress || undefined,
        status: newAddress ? 'matched' : 'skipped',
      };
      const matched = updatedDetails.filter(d => d.status === 'matched').length;
      const skipped = updatedDetails.filter(d => d.status === 'skipped').length;
      setResults({ ...results, details: updatedDetails, matched, skipped });
      setEditingIndex(null);
      setEditValue("");
      setEditName("");
      setEditDate("");
      toast.info("No DB record found for that name/date — updated locally");
      return;
    }

    setSavingIndex(index);

    try {
      const { error } = await supabase
        .from('county_research')
        .update({
          grantor_address: newAddress,
          address_extraction_method: newAddress ? 'worksheet_scan_manual' : null,
          address_needs_review: false,
        })
        .eq('id', dbId);

      if (error) throw error;

      const updatedDetails = [...results.details];
      updatedDetails[index] = {
        ...updatedDetails[index],
        dbId,
        name: lookupName || updatedDetails[index].name,
        filingDate: normalizedDate || lookupDate || updatedDetails[index].filingDate,
        address: newAddress || undefined,
        status: newAddress ? 'saved' : 'skipped',
      };
      const notFound = updatedDetails.filter(d => d.status === 'not_found').length;
      setResults({ ...results, details: updatedDetails, notFound });
      setTotalSaved(prev => prev + 1);
      setEditingIndex(null);
      setEditValue("");
      setEditName("");
      setEditDate("");
      toast.success(`✅ Matched & saved: ${lookupName || row.name}`);
      onRecordsUpdated?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to save");
    } finally {
      setSavingIndex(null);
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const row = results?.details[index];
      if (row?.status === 'not_found' || !row?.dbId || (integrated && row?.dbId)) {
        saveEditWithLookup(index);
      } else {
        saveEdit(index);
      }
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case 'matched': return <AlertCircle className="h-4 w-4 text-amber-500" />;
      case 'saved': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'skipped': return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
      case 'not_found': return <XCircle className="h-4 w-4 text-yellow-600" />;
      case 'error': return <XCircle className="h-4 w-4 text-destructive" />;
      default: return null;
    }
  };

  const matchedWithAddresses = results?.details.filter(r => (r.status === 'matched' || r.status === 'not_found') && r.address).length || 0;

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Scan Handwritten Address Worksheet
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Upload each worksheet photo. Review the extracted addresses, then click <strong>"Save to Database"</strong> to commit.
          </p>
        </div>
        {results && !integrated && (
          <Button variant="outline" size="sm" onClick={handleClearResults} className="text-muted-foreground shrink-0">
            <Trash2 className="mr-2 h-4 w-4" />
            Clear Results
          </Button>
        )}
      </div>

      {sheetsScanned > 0 && (
        <div className="bg-muted/50 rounded-lg p-3 flex items-center gap-4 text-sm">
          <span className="font-medium">Session Progress:</span>
          <Badge variant="secondary">{sheetsScanned} sheet{sheetsScanned !== 1 ? 's' : ''} scanned</Badge>
          <Badge variant="default" className="bg-green-600">{totalSaved} total saved to DB</Badge>
        </div>
      )}

      <div
        className="border-2 border-dashed border-border rounded-lg p-8 text-center focus:ring-2 focus:ring-ring focus:outline-none"
        onPaste={handlePaste}
        tabIndex={0}
      >
        {!imageData ? (
          <div className="space-y-3">
            <Camera className="h-12 w-12 mx-auto text-muted-foreground" />
            <p className="text-muted-foreground">
              {sheetsScanned > 0
                ? `Ready for sheet ${sheetsScanned + 1} — upload or paste the next worksheet photo`
                : "Upload a photo of the worksheet or paste from clipboard"}
            </p>
            <div className="flex gap-2 justify-center">
              <label>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileUpload} />
                <Button variant="outline" size="sm" asChild>
                  <span><Upload className="mr-2 h-4 w-4" />Upload Photo</span>
                </Button>
              </label>
              <label>
                <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                <Button variant="outline" size="sm" asChild>
                  <span>📁 Choose File</span>
                </Button>
              </label>
            </div>
            <p className="text-xs text-muted-foreground">Or click here and press Ctrl+V to paste</p>
          </div>
        ) : (
          <div className="space-y-4">
            <img src={imageData} alt="Worksheet photo" className="max-h-96 mx-auto rounded border" />
            <div className="flex gap-2 justify-center">
              {!results ? (
                <>
                  <Button onClick={handleScan} disabled={isProcessing} size="lg">
                    {isProcessing ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Reading Handwriting...</>
                    ) : (
                      <><CheckCircle className="mr-2 h-4 w-4" />Scan & Preview</>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => setImageData(null)} disabled={isProcessing}>
                    Clear
                  </Button>
                </>
              ) : (
                <>
                  {!integrated && matchedWithAddresses > 0 && (
                    <Button onClick={handleIntegrate} disabled={isSaving} size="lg" className="bg-green-600 hover:bg-green-700">
                      {isSaving ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
                      ) : (
                        <><Database className="mr-2 h-4 w-4" />Save {matchedWithAddresses} to Database</>
                      )}
                    </Button>
                  )}
                  <Button onClick={handleNextSheet} size="lg" variant={integrated ? "default" : "outline"}>
                    <ArrowRight className="mr-2 h-4 w-4" />
                    Scan Next Sheet
                  </Button>
                  {onViewUpdatedRecords && integrated && (
                    <Button variant="outline" onClick={onViewUpdatedRecords}>
                      <Eye className="mr-2 h-4 w-4" />
                      View Updated Records
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {results && (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap items-center">
            {!integrated && results.matched > 0 && (
              <Badge variant="outline" className="text-amber-600 border-amber-300">
                {results.matched} Ready to Save
              </Badge>
            )}
            {integrated && (
              <Badge variant="default" className="bg-green-600">
                ✅ {totalSaved} Saved to DB
              </Badge>
            )}
            <Badge variant="secondary">{results.skipped} Blank</Badge>
            {results.notFound > 0 && <Badge variant="outline" className="text-yellow-600">{results.notFound} Not Found</Badge>}
            <span className="text-xs text-muted-foreground ml-2">
              {integrated ? "Saved to database. Edit with pencil icon." : "⚠️ Not yet saved — review and click Save to Database"}
            </span>
          </div>

          <div className="max-h-80 overflow-y-auto border rounded">
            <table className="w-full text-sm">
              <thead className="bg-muted sticky top-0">
              <tr>
                  <th className="text-left p-2 w-10">Status</th>
                  <th className="text-left p-2 w-24">Date</th>
                  <th className="text-left p-2 w-24">Doc #</th>
                  <th className="text-left p-2">Seller</th>
                  <th className="text-left p-2">Address</th>
                  <th className="text-left p-2 w-28">Match</th>
                  <th className="text-left p-2 w-16">Edit</th>
                </tr>
              </thead>
              <tbody>
                {results.details.map((r, i) => (
                  <tr key={i} className={`border-t ${r.status === 'not_found' ? 'bg-yellow-50' : r.status === 'matched' && !integrated ? 'bg-amber-50' : r.status === 'saved' ? 'bg-green-50' : ''}`}>
                    <td className="p-2">{statusIcon(r.status)}</td>
                    <td className="p-2 font-mono text-xs">
                      {editingIndex === i ? (
                        <Input
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                          className="h-7 text-xs w-24"
                          placeholder="YYYY-MM-DD"
                        />
                      ) : (r.filingDate || '-')}
                    </td>
                    <td className="p-2 font-mono text-xs">{r.doc}</td>
                    <td className="p-2">
                      {editingIndex === i ? (
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="h-7 text-sm"
                          placeholder="Type last name (before comma)"
                          autoFocus
                        />
                      ) : (r.name || '-')}
                    </td>
                    <td className="p-2">
                      {editingIndex === i ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleEditKeyDown(e, i)}
                            className="h-7 text-sm"
                            placeholder="Enter address or leave blank to clear"
                            autoFocus
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 shrink-0"
                            onClick={() => saveEditWithLookup(i)}
                            disabled={savingIndex === i}
                          >
                            {savingIndex === i ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 text-green-600" />}
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={cancelEdit}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        r.address || <span className="text-muted-foreground italic">{r.col5 || r.reason}</span>
                      )}
                    </td>
                    <td className="p-2 text-xs text-muted-foreground">{r.matchMethod || '-'}</td>
                    <td className="p-2">
                      {editingIndex !== i && (
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(i, r.address)}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => {
                            const updatedDetails = results!.details.filter((_, idx) => idx !== i);
                            const matched = updatedDetails.filter(d => d.status === 'matched').length;
                            const skipped = updatedDetails.filter(d => d.status === 'skipped').length;
                            const notFound = updatedDetails.filter(d => d.status === 'not_found').length;
                            setResults({ matched, skipped, notFound, details: updatedDetails });
                          }}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}
